import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { get } from 'lodash';

import { Button, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import type { BannerMessage } from '@libraries/api/gen-api-hidden';

import { StatusBanner, YubiIcon } from '@atoms';

import Footer from './Footer';

import style from './LoginV2.module.scss';

import type { RequestState } from '../../../../types/types';

import { isServiceConsole, SERVICE_CONSOLE_REFERRER } from '../../../helpers';

import { api, basePath } from '../../../api';
import BigLogo from '../../../media/images/landingPageSplash/Yubico_logo_white.png';

function SplashScreen() {
  return (
    <>
      <div className={style.flyingKey} />
      <div className={style.logoContainer}>
        <img className={style.bigLogo} alt="Yubico Logo" src={BigLogo} />
      </div>
    </>
  );
}

function LoginV2() {
  const { t } = useTranslation();

  const loginLink = isServiceConsole
    ? `${window.fromServer.env.LOGIN_HOST_URL}?referrer_app=${SERVICE_CONSOLE_REFERRER}`
    : window.fromServer.env.LOGIN_HOST_URL;

  const [banners, setBanners] = useState<Array<BannerMessage>>();
  const [fetchingBanners, setFetchingBanners] = useState<RequestState>('idle');
  const fetchBanners = () =>
    api.BannersApi.getBanners()
      .then((response) => {
        setBanners(get(response, ['data'], []));
        setFetchingBanners('resolved');
      })
      .catch(() => {
        setFetchingBanners('rejected');
      });

  useEffect(() => {
    if (fetchingBanners === 'idle') {
      // For serviceconsole, IAP redirect is not required
      if (isServiceConsole) {
        fetchBanners();
        return;
      }
      const iapUrl = `${basePath}/iap`;
      // If this call results in a redirect it means the caller _does_ not have the appropriate
      // IAP cookies. `redirect: error` will raise an error if it redirects.
      fetch(iapUrl, { redirect: 'error', method: 'HEAD', credentials: 'include' })
        .then(() => {
          fetchBanners();
        })
        .catch((error) => {
          // No valid IAP cookies were set, load api.console.<env>.yubico.com/iap which will
          // refresh the IAP cookies for the API host and then relocate to this page again.
          if (error instanceof TypeError) {
            window.location.assign(iapUrl);
          }
        });
    }
  }, [fetchingBanners]);

  return (
    <div className={style.mainArea}>
      <Box className={style.leftPanel}>
        <div className={style.contentContainer}>
          <div className={style.logoSection}>
            <Link to="/login">
              <YubiIcon icon="Logo" height="40" width="143" />
            </Link>
          </div>
          <StatusBanner banners={banners} className={style.bannersSection} />
          <div className={style.welcomeSection}>
            <Typography
              className={cn(style.title, {
                [style.internalTitle]: isServiceConsole,
              })}
              variant="h4"
              component="h3"
            >
              {isServiceConsole
                ? t('login_page.service_console_title')
                : t('login_page.enterprise_console_title')}
            </Typography>
            <Typography variant="body1">{t('login_page.sub_title')}</Typography>
            <Button component="a" href={loginLink} className={style.loginButton}>
              <Trans i18nKey="login" />
            </Button>
            <div>
              <a href="https://www.yubico.com/products/yubienterprise/">
                <Typography variant="body1" className={style.link}>
                  <Trans i18nKey="learn_more" />
                </Typography>
              </a>
              <Typography variant="body1">
                {`${t('login_page.service_title')} `}
                <a className={style.link} href="https://www.yubico.com/support/contact/">
                  {t('login_page.contact_sales')}
                </a>
                .
              </Typography>
            </div>
          </div>
        </div>
        <Footer />
      </Box>
      <Box className={style.rightPanel}>
        <SplashScreen />
      </Box>
    </div>
  );
}

export default LoginV2;
